import { useWorkspaceText } from '../interface-copy';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { MailIcon } from 'lucide-react';
import { Segmented } from '@/components/ui/kit';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { overlaysCopy, type OverlaysCopyKey } from '@/i18n/ns/overlays';
import agentPresentationCopy from '@/i18n/ns/agent-presentation';
import editorCopy from '@/i18n/ns/workspace-editors';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { WorkspaceMarkdown } from '../markdown';
import { emptyTurnContext } from '../types';
import { agentsApi, type Agent, type AgentResultMode } from './api';
import { AgentConnectionSetup } from './connection-setup';
import { OptionalSkills } from './definition-fields';
import { makingNewAgent } from './new-agent';
import {
  describeSchedule,
  formSchedule,
  placeOf,
  scheduleForm,
  type ScheduleForm,
} from './schedule';
import { ScheduleFields } from './setup-fields';
import type { Act } from './shared';

// The Agent tab (agent redesign 2026-10-09): the agent as readable parts, each
// changed in place by people who edit it. Goal, When it runs, Instructions,
// Skills, Uses and Delivers map onto the one definition every save restates
// whole (PUT definition: a new version, or the draft of an agent never
// created), and onto its independent schedule trigger.

export type AgentPart = 'goal' | 'schedule' | 'instructions' | 'skills' | 'delivers';

const headOf = (agent: Agent) => (agent.draft ?? agent.published)!;

/** The whole definition, as every save restates it (the server's agentUpdateInput). */
export function definitionOf(agent: Agent) {
  const head = headOf(agent);
  return {
    name: agent.name,
    description: agent.description,
    goal: head.goal ?? '',
    instructions: head.instructions,
    expectedOutput: head.expectedOutput ?? '',
    boundaries: head.boundaries ?? '',
    resources: head.resources ?? '',
    skillIds: head.skillIds ?? (head.skill?.id ? [head.skill.id] : []),
    emailOnFinish: head.emailOnFinish,
    resultMode: head.resultMode,
  };
}
type Definition = ReturnType<typeof definitionOf>;

type PartProps = {
  agent: Agent;
  busy: boolean;
  editable: boolean;
  editing: AgentPart | null;
  values: Definition;
  change: (patch: Partial<Definition>) => void;
  open: (part: AgentPart) => void;
  close: () => void;
  /** Saves the whole definition with `patch`: from an open editor at the revision it opened on,
   * so a change made meanwhile is a conflict rather than overwritten. */
  saveDefinition: (patch: Partial<Definition>, done?: string) => Promise<void>;
};

function Block({
  part,
  title,
  action,
  blockRef,
  children,
}: {
  part: string;
  title: string;
  action?: ReactNode;
  blockRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  return (
    <section
      ref={blockRef}
      className="ws-agents-block"
      data-part={part}
      aria-labelledby={`agent-${part}-title`}
    >
      <div className="ws-agents-block-head">
        <h2 id={`agent-${part}-title`}>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** The quiet "Change" beside a part's title, for people who edit the agent. */
function ChangeLink({
  props,
  part,
  title,
  label = 'Change',
}: {
  props: PartProps;
  part: AgentPart;
  title: string;
  label?: string;
}) {
  if (!props.editable || props.editing === part) return null;
  return (
    <button
      type="button"
      className="ws-agents-block-action"
      aria-label={`${label}: ${title}`}
      disabled={props.busy}
      onClick={() => props.open(part)}
    >
      {label}
    </button>
  );
}

function Editor({
  label,
  props,
  canSave,
  onSave,
  children,
}: {
  label: string;
  props: Pick<PartProps, 'busy' | 'close'>;
  canSave: boolean;
  onSave: () => void;
  children: ReactNode;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const save = () => {
    if (canSave && !props.busy) onSave();
  };
  return (
    <form
      className="ws-agents-form ws-agents-editor"
      aria-label={label}
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      {children}
      <span className="ws-actions">
        <button
          type="button"
          className="ws-button small"
          disabled={props.busy}
          onClick={props.close}
        >
          {uiText('Cancel')}
        </button>
        <button
          type="submit"
          className="ws-button primary small"
          disabled={props.busy || !canSave}
          onClick={(event) => {
            event.preventDefault();
            save();
          }}
        >
          {uiText('Save')}
        </button>
      </span>
    </form>
  );
}

function TextField({
  label,
  hint,
  value,
  max,
  rows,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  max: number;
  /** A text area with this many rows; a one-line field without. */
  rows?: number;
  onChange: (value: string) => void;
}) {
  return (
    <label className="ws-agents-field">
      <span>{label}</span>
      {hint && <small>{hint}</small>}
      {rows ? (
        <textarea
          aria-label={label}
          value={value}
          rows={rows}
          maxLength={max}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          type="text"
          aria-label={label}
          value={value}
          maxLength={max}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </label>
  );
}

function GoalPart(props: PartProps) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const { agent, values } = props;
  const goal = headOf(agent).goal;
  const description = agent.description;
  return (
    <Block
      part="goal"
      title={uiText('Goal')}
      action={<ChangeLink props={props} part="goal" title={uiText('Goal')} />}
    >
      {props.editing === 'goal' ? (
        <Editor
          label={uiText('Change the goal')}
          props={props}
          canSave={Boolean(values.name.trim())}
          onSave={() =>
            void props.saveDefinition({
              name: values.name.trim(),
              description: values.description,
              goal: values.goal,
            })
          }
        >
          <TextField
            label={uiText('Name')}
            value={values.name}
            max={120}
            onChange={(name) => props.change({ name })}
          />
          <TextField
            label={uiText('What it does')}
            value={values.description}
            max={2000}
            onChange={(next) => props.change({ description: next })}
          />
          <TextField
            label={uiText('Goal')}
            hint={uiText('What should this agent achieve?')}
            value={values.goal}
            max={2000}
            rows={3}
            onChange={(next) => props.change({ goal: next })}
          />
        </Editor>
      ) : (
        <>
          <p className="ws-agents-block-text">
            {goal || description || <span className="ws-muted">{uiText('Not set yet.')}</span>}
          </p>
          {goal && description && description !== goal && (
            <p className="ws-agents-block-note">{description}</p>
          )}
        </>
      )}
    </Block>
  );
}

/** "Weekdays at 07:00, Stockholm time. Anyone at Norrvik can also run it now." */
function scheduleSentence(
  agent: Agent,
  companyName: string,
  copy: Readonly<Record<OverlaysCopyKey, string>>,
  agentCopy: Readonly<Record<string, string>>,
  locale: string,
) {
  const schedule = agent.trigger?.schedule ?? null;
  const company = agent.scope === 'company';
  if (!schedule)
    return company
      ? `${agentCopy.onDemand} ${formatCopy(agentCopy.anyoneCanRun, { company: companyName })}`
      : `${agentCopy.onDemand} ${agentCopy.youRun}`;
  const also = company
    ? formatCopy(agentCopy.alsoCompany, { company: companyName })
    : agentCopy.alsoRun;
  return `${describeSchedule(schedule, copy, locale)}, ${placeOf(schedule.timezone)} time. ${also}`;
}

function SchedulePart({
  props,
  draft,
  schedule,
  setSchedule,
  changed,
  save,
  blockRef,
}: {
  props: PartProps;
  /** A draft chooses its schedule in place; a created agent opens the editor with Change. */
  draft: boolean;
  schedule: ScheduleForm;
  setSchedule: (next: ScheduleForm) => void;
  changed: boolean;
  save: () => void;
  blockRef: RefObject<HTMLElement | null>;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const agentCopy = useNamespaceCopy(agentPresentationCopy);
  const copy = useNamespaceCopy(overlaysCopy);
  const locale = useChatInterfaceLocale();
  const inPlace = draft && props.editable;
  const fields = (
    <ScheduleFields value={schedule} disabled={props.busy} onChange={setSchedule} bare />
  );
  return (
    <Block
      part="schedule"
      title={uiText('When it runs')}
      blockRef={blockRef}
      action={
        inPlace ? null : <ChangeLink props={props} part="schedule" title={uiText('When it runs')} />
      }
    >
      {inPlace ? (
        <div className="ws-agents-schedule-draft">
          {fields}
          {changed && (
            <span className="ws-actions">
              <button
                type="button"
                className="ws-button small"
                disabled={props.busy}
                onClick={props.close}
              >
                {uiText('Cancel')}
              </button>
              <button
                type="button"
                className="ws-button primary small"
                disabled={props.busy}
                onClick={save}
              >
                {uiText('Save')}
              </button>
            </span>
          )}
        </div>
      ) : props.editing === 'schedule' ? (
        <Editor label={uiText('Change when it runs')} props={props} canSave={changed} onSave={save}>
          {fields}
          <small className="ws-muted">
            {uiText(
              'Off means it runs only when someone runs it. Changing when it runs never changes its instructions.',
            )}
          </small>
        </Editor>
      ) : (
        <p className="ws-agents-block-text">
          {scheduleSentence(props.agent, ws.companyName, copy, agentCopy, locale)}
        </p>
      )}
    </Block>
  );
}

function InstructionsPart(props: PartProps) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const { agent, values } = props;
  const head = headOf(agent);
  return (
    <Block
      part="instructions"
      title={uiText('Instructions')}
      action={<ChangeLink props={props} part="instructions" title={uiText('Instructions')} />}
    >
      {props.editing === 'instructions' ? (
        <Editor
          label={uiText('Change the instructions')}
          props={props}
          canSave
          onSave={() =>
            void props.saveDefinition({
              instructions: values.instructions,
              boundaries: values.boundaries,
            })
          }
        >
          <TextField
            label={uiText('Instructions')}
            hint={uiText('How the agent should approach the work and make decisions.')}
            value={values.instructions}
            max={8000}
            rows={7}
            onChange={(instructions) => props.change({ instructions })}
          />
          <TextField
            label={uiText('Boundaries')}
            hint={uiText('What to do or avoid. Every connected action still follows its approval.')}
            value={values.boundaries}
            max={2000}
            rows={3}
            onChange={(boundaries) => props.change({ boundaries })}
          />
        </Editor>
      ) : (
        <div className="ws-agents-block-document">
          {head.instructions ? (
            <WorkspaceMarkdown text={head.instructions} />
          ) : (
            <p className="ws-muted">
              {uiText('No instructions of its own. It follows its Skills.')}
            </p>
          )}
          {head.boundaries && (
            <div className="ws-agents-boundaries">
              <span>{uiText('Boundaries')}</span> <WorkspaceMarkdown text={head.boundaries} />
            </div>
          )}
        </div>
      )}
    </Block>
  );
}

function SkillsPart(props: PartProps) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const head = headOf(props.agent);
  const skills =
    head.skills ??
    head.skillIds.map((id) => ({ id, name: null, published: false, publishedRevision: null }));
  return (
    <Block
      part="skills"
      title={uiText('Skills')}
      action={
        <ChangeLink
          props={props}
          part="skills"
          title={uiText('Skills')}
          label={uiText('Pin a Skill')}
        />
      }
    >
      <p className="ws-agents-block-text">
        {uiText('Finds the Skills that fit each run, the same way a chat does.')}
      </p>
      {props.editing === 'skills' ? (
        <Editor
          label={uiText('Skills it always follows')}
          props={props}
          canSave
          onSave={() => void props.saveDefinition({ skillIds: props.values.skillIds })}
        >
          <OptionalSkills
            value={props.values.skillIds}
            disabled={props.busy}
            onChange={(skillIds) => props.change({ skillIds })}
            legend="Always follows"
            note="A pinned Skill is followed on every run, at its published version."
          />
        </Editor>
      ) : (
        skills.map((skill) => (
          <div className="ws-agents-line" key={skill.id}>
            <WorkspaceIcon name="skills" size={16} />
            {skill.name ? (
              <button
                type="button"
                className="ws-agents-line-link"
                onClick={() => ws.openCanvas({ kind: 'skill', id: skill.id })}
              >
                {skill.name}
              </button>
            ) : (
              <span className="ws-muted">{uiText('A Skill that is not published')}</span>
            )}{' '}
            <span className="ws-agents-line-meta">
              {skill.publishedRevision
                ? uiText('Always follows · Version {value1}', { value1: skill.publishedRevision })
                : uiText('Not published')}
            </span>
          </div>
        ))
      )}
    </Block>
  );
}

function UsesPart(props: PartProps & { draft: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const { agent } = props;
  const editor = useNamespaceCopy(editorCopy);
  const head = headOf(agent);
  const ownerHere = Boolean(ws.user && agent.isOwner && agent.owner?.userId === ws.user.id);
  // In the chat making this draft, the person says what to change; elsewhere the
  // chat is asked to read the agent and propose the change.
  const changeInChat = () =>
    props.draft && makingNewAgent(ws.turnContext)
      ? ws.send(
          formatCopy(editor.changeAgentUsesPrompt, { id: agent.id, name: agent.name }),
          ws.turnContext,
          false,
        )
      : ws.send(
          formatCopy(editor.changeAgentUsesPrompt, { id: agent.id, name: agent.name }),
          emptyTurnContext(),
          true,
        );
  return (
    <Block
      part="uses"
      title={uiText('Uses')}
      action={
        props.editable ? (
          <button
            type="button"
            className="ws-agents-block-action"
            aria-label={uiText('Change in chat: Uses')}
            onClick={changeInChat}
          >
            {uiText('Change in chat')}
          </button>
        ) : null
      }
    >
      <div className="ws-agents-block-document">
        {head.resources ? (
          <WorkspaceMarkdown text={head.resources} />
        ) : (
          <p className="ws-muted">
            {uiText('Company knowledge and the tools each run is allowed to use.')}
          </p>
        )}
      </div>
      {props.editable && ownerHere && <AgentConnectionSetup key={`${agent.id}:${ws.user?.id}`} />}
    </Block>
  );
}

function ResultNote({ agent, mode }: { agent: Agent; mode: AgentResultMode }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  if (mode !== 'keep_updated') return null;
  return (
    <small className="ws-muted">
      {agent.maintainedResultId
        ? uiText('People see the Result refresh; they change the agent, not the page.')
        : uiText(
            'The first run after it is created makes the Result; later runs add a version to it.',
          )}
      {agent.scope === 'personal' &&
        ' ' +
          uiText(
            'It starts private. Sharing it wider shares every refresh, which can carry your private data.',
          )}
    </small>
  );
}

function EmailLine(props: PartProps) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const on = headOf(props.agent).emailOnFinish;
  return (
    <div className="ws-agents-line">
      <MailIcon aria-hidden="true" />
      <span>{uiText('Emails whoever the run is for')}</span>{' '}
      <span className="ws-agents-line-meta ws-agents-switch">
        <input
          type="checkbox"
          role="switch"
          aria-label={uiText('Email each run')}
          checked={on}
          // A one-click change waits while a part is being changed.
          disabled={!props.editable || props.busy || props.editing !== null}
          onChange={(event) =>
            void props.saveDefinition(
              { emailOnFinish: event.target.checked },
              event.target.checked
                ? 'Each run now emails whoever it ran for.'
                : 'Runs no longer send an email.',
            )
          }
        />
      </span>
    </div>
  );
}

function DeliversPart(props: PartProps & { draft: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const { agent, values } = props;
  const editor = useNamespaceCopy(editorCopy);
  const resultChoices: { value: AgentResultMode; label: string }[] = [
    { value: 'new', label: editor.newResultEachRun },
    { value: 'keep_updated', label: editor.keepResultUpdated },
  ];
  const head = headOf(agent);
  const kept = agent.maintainedResultId;
  const keptTitle = kept ? ws.data.results.find((result) => result.id === kept)?.title : null;
  const inPlace = props.draft && props.editable;
  return (
    <Block
      part="delivers"
      title={uiText('Delivers')}
      action={
        inPlace ? null : <ChangeLink props={props} part="delivers" title={uiText('Delivers')} />
      }
    >
      {props.editing === 'delivers' ? (
        <Editor
          label={uiText('Change what it delivers')}
          props={props}
          canSave
          onSave={() =>
            void props.saveDefinition({
              expectedOutput: values.expectedOutput,
              resultMode: values.resultMode,
            })
          }
        >
          <TextField
            label={editor.expectedResult}
            hint={editor.expectedResultHint}
            value={values.expectedOutput}
            max={2000}
            rows={3}
            onChange={(expectedOutput) => props.change({ expectedOutput })}
          />
          <Segmented
            label={editor.whatEachRunDelivers}
            options={resultChoices}
            value={values.resultMode}
            disabled={props.busy}
            onChange={(resultMode) => props.change({ resultMode })}
          />
          <ResultNote agent={agent} mode={values.resultMode} />
        </Editor>
      ) : (
        <>
          {head.expectedOutput && (
            <div className="ws-agents-block-document">
              <WorkspaceMarkdown text={head.expectedOutput} />
            </div>
          )}
          {inPlace ? (
            <Segmented
              label={editor.whatEachRunDelivers}
              options={resultChoices}
              value={head.resultMode}
              disabled={props.busy || props.editing !== null}
              onChange={(resultMode) => void props.saveDefinition({ resultMode })}
            />
          ) : (
            <div className="ws-agents-line">
              <WorkspaceIcon name="results" size={16} />
              {head.resultMode === 'keep_updated' ? (
                <span>
                  {uiRich('Keeps one Result up to date {value1}', {
                    value1: kept ? (
                      <>
                        :{' '}
                        <button
                          type="button"
                          className="ws-agents-line-link"
                          onClick={() => ws.openCanvas({ kind: 'result', id: kept })}
                        >
                          {keptTitle ?? uiText('its Result')}
                        </button>
                      </>
                    ) : (
                      <span className="ws-muted">{uiText('· its first run makes it')}</span>
                    ),
                  })}
                </span>
              ) : (
                <span>{uiText('Saves a new Result each run')}</span>
              )}
            </div>
          )}
          <EmailLine {...props} />
        </>
      )}
    </Block>
  );
}

function TopNotes({ agent, onReviewDraft }: { agent: Agent; onReviewDraft: () => void }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const editor = useNamespaceCopy(editorCopy);
  if (!agent.access.edit)
    return (
      <p className="ws-agents-note">
        <WorkspaceIcon name="eye" size={14} />
        <span>
          {uiRich('You can run this company agent as yourself.{value1} {value2}', {
            value1: ' ',
            value2:
              agent.myEditAccess === 'requested'
                ? editor.editAccessRequested
                : editor.askForEditAccess,
          })}
        </span>
      </p>
    );
  if (!agent.draft || agent.publishedRevision === null) return null;
  return (
    <p className="ws-agents-note">
      <WorkspaceIcon name="history" size={14} />
      <span>
        {uiText('Changes since version')} {agent.publishedRevision} {uiText('are not live yet.')}{' '}
        <button type="button" className="ws-text-button" onClick={onReviewDraft}>
          {uiText('Review them in Versions')}
        </button>
      </span>
    </p>
  );
}

export function AgentSections({
  agent,
  busy,
  act,
  draft,
  focus,
  onReviewDraft,
}: {
  agent: Agent;
  busy: boolean;
  act: Act;
  /** An agent never created, being made: its schedule and Result are chosen in place. */
  draft: boolean;
  /** The part the canvas was opened on (an older Triggers link opens When it runs). */
  focus: AgentPart | null;
  onReviewDraft: () => void;
}) {
  const ws = useWorkspace();
  const agentCopy = useNamespaceCopy(agentPresentationCopy);
  const saved = definitionOf(agent);
  const savedKey = JSON.stringify(saved);
  const savedSchedule = agent.trigger?.schedule ?? null;
  const savedScheduleKey = JSON.stringify(formSchedule(scheduleForm(savedSchedule)));
  const [editing, setEditing] = useState<AgentPart | null>(null);
  // The revision an open editor started from.
  const [base, setBase] = useState(agent.currentRevision);
  const [values, setValues] = useState<Definition>(saved);
  const [schedule, setSchedule] = useState<ScheduleForm>(() => scheduleForm(savedSchedule));
  // Saves and changes made elsewhere (the chat, a colleague) arrive here, except into the
  // part being edited.
  useEffect(() => {
    if (editing === null) setValues(JSON.parse(savedKey));
  }, [savedKey, editing]);
  useEffect(() => {
    if (editing !== 'schedule') setSchedule(scheduleForm(JSON.parse(savedScheduleKey)));
  }, [savedScheduleKey, editing]);
  const scheduleChanged = JSON.stringify(formSchedule(schedule)) !== savedScheduleKey;
  const dirty =
    (editing !== null && editing !== 'schedule' && JSON.stringify(values) !== savedKey) ||
    ((editing === 'schedule' || draft) && scheduleChanged);
  useEffect(() => {
    ws.setCanvasDirty(dirty);
    return () => ws.setCanvasDirty(false);
  }, [dirty]);
  const scheduleRef = useRef<HTMLElement | null>(null);
  // After the canvas has taken its own first focus (canvas-focus.ts), land on When it runs.
  useEffect(() => {
    if (focus !== 'schedule') return;
    const timer = setTimeout(() => {
      scheduleRef.current?.scrollIntoView?.({ block: 'start' });
      scheduleRef.current?.querySelector<HTMLElement>('button')?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [focus]);
  const open = (part: AgentPart) =>
    ws.confirmLeave(() => {
      setValues(JSON.parse(savedKey));
      setSchedule(scheduleForm(savedSchedule));
      setBase(agent.currentRevision);
      setEditing(part);
    });
  const close = () => {
    setEditing(null);
    setSchedule(scheduleForm(savedSchedule));
  };
  const savedNotice =
    agent.publishedRevision === null
      ? agentCopy.draftSaved
      : formatCopy(agentCopy.savedVersion, { version: agent.currentRevision + 1 });
  const saveDefinition: PartProps['saveDefinition'] = (patch, done = savedNotice) =>
    act(async () => {
      await agentsApi.update(agent.id, {
        ...saved,
        ...patch,
        baseRevision: editing === null ? agent.currentRevision : base,
      });
      setEditing(null);
    }, done);
  const saveSchedule = () => {
    const next = formSchedule(schedule);
    void act(
      async () => {
        await agentsApi.setTrigger(agent.id, {
          schedule: next,
          enabled: Boolean(next) && agent.enabled,
        });
        setEditing(null);
      },
      next ? agentCopy.scheduleSaved : agentCopy.onDemandNow,
    );
  };
  const props: PartProps = {
    agent,
    busy,
    editable: agent.access.edit,
    editing,
    values,
    change: (patch) => setValues((current) => ({ ...current, ...patch })),
    open,
    close,
    saveDefinition,
  };
  return (
    <div className="ws-agents-sections">
      <TopNotes agent={agent} onReviewDraft={onReviewDraft} />
      <GoalPart {...props} />
      <SchedulePart
        props={props}
        draft={draft}
        schedule={schedule}
        setSchedule={setSchedule}
        changed={scheduleChanged}
        save={saveSchedule}
        blockRef={scheduleRef}
      />
      <InstructionsPart {...props} />
      <SkillsPart {...props} />
      <UsesPart {...props} draft={draft} />
      <DeliversPart {...props} draft={draft} />
    </div>
  );
}

/** The parts a new agent will have, while its chat describes it (nothing saved yet). */
export const NEW_AGENT_PARTS = [
  { part: 'goal', title: 'Goal', hint: 'What it should achieve.' },
  { part: 'schedule', title: 'When it runs', hint: 'On demand, or on a schedule you choose.' },
  { part: 'instructions', title: 'Instructions', hint: 'How it should do the work.' },
  {
    part: 'skills',
    title: 'Skills',
    hint: 'Finds the Skills that fit each run, the same way a chat does.',
  },
  { part: 'uses', title: 'Uses', hint: 'Company knowledge and the connections it may use.' },
  { part: 'delivers', title: 'Delivers', hint: 'A Result: new each run, or one kept up to date.' },
] as const;

export function NewAgentParts() {
  const { text: uiText } = useWorkspaceText();
  const editor = useNamespaceCopy(editorCopy);
  const hints = {
    goal: editor.newAgentGoalHint,
    schedule: editor.newAgentScheduleHint,
    instructions: editor.newAgentInstructionsHint,
    skills: editor.newAgentSkillsHint,
    uses: editor.newAgentUsesHint,
    delivers: editor.newAgentDeliversHint,
  } as const;
  return (
    <div className="ws-agents-sections">
      {NEW_AGENT_PARTS.map(({ part, title, hint }) => (
        <Block key={part} part={part} title={uiText(title)}>
          <p className="ws-agents-block-text ws-muted">{hints[part as keyof typeof hints]}</p>
        </Block>
      ))}
    </div>
  );
}
